import type { Continuity, Person, StoryJournalEntry } from '@dissidium/contracts';
import { closeRun } from '../continuity/eligibility.ts';
import { assertContinuityTransition, bindLiving, changeRemains, demanifest, killPerson, registerPerson, validateLedger } from '../continuity/ledger.ts';
import { OPENING_DATE } from '../continuity/calendar.ts';
import { applyEffects } from '../quests/graph.ts';
import { makePerson } from '../seed/people.ts';
import { createStorySession, type StoryContent, type StorySession } from './story.ts';

export const storyPcId = (state: StorySession): string => state.bindings?.['person:pc'] ?? 'person:pc';
export const storyIdentity = (state: StorySession, id: string): string => state.bindings?.[id] ?? id;
export function storyText(state: StorySession, text: string): string {
  for (const [from, to] of Object.entries(state.castNames ?? {})) text = text.replaceAll(from, to);
  return text;
}

/** Both durable and prototype stories expose only records and people in their actual ledger. */
export function withContinuityLeads(content: StoryContent, state: StorySession): StorySession {
  const continuity = state.slice.continuity;
  const previous = continuity.closedRuns.filter(run => run.pcId !== storyPcId(state));
  const survivor = [...previous].reverse().find(run => run.outcome === 'survived'
    && continuity.legacyIds.includes(run.pcId) && continuity.persons[run.pcId]?.lifecycle === 'active');
  const record = [...continuity.echoes].reverse().find(record => record.id.endsWith(':staple-comparison')
    && previous.some(run => run.id === record.runId));
  // What the life before this one left behind (chapter `remembered`), whoever lived it.
  const last = continuity.persons[previous.at(-1)?.pcId ?? '']?.current.deeds ?? [];
  const past = (content.remembered ?? []).filter(memory => last.includes(memory.fact)).map(memory => `past:${memory.fact}`);
  const add = [
    ...past,
    ...(survivor ? ['legacy:survivor-present'] : []),
    ...(record ? ['legacy:record-available'] : []),
    ...(previous.at(-1)?.outcome === 'died' ? ['legacy:last-died'] : []),
    ...(previous.length && continuity.persons['person:leth-an']?.lifecycle === 'dead' ? ['mystery:earlier-account'] : []),
  ];
  const { legacyId: _legacy, recordId: _record, ...rest } = state;
  return {
    ...rest, ...(survivor ? { legacyId: survivor.pcId } : {}), ...(record ? { recordId: record.id } : {}),
    slice: { ...state.slice, quest: applyEffects(state.slice.quest, {
      add, remove: [...state.slice.quest.facts.filter(fact => fact.startsWith('past:')), 'legacy:survivor-present', 'legacy:record-available', 'legacy:last-died', 'mystery:earlier-account'].filter(fact => !add.includes(fact)),
    }) },
  };
}

/** A life ends at the still place once the Silt Stair aftermath is witnessed, unless it took the regional
 * crossing (campaign B2): then only its departure (B6) ends it. */
export const runEnded = (facts: readonly string[]): boolean =>
  facts.includes('fact:local-aftermath-observed') && (!facts.includes('campaign:B2') || facts.includes('campaign:B6'));

/** A later life whose concession reopened work an earlier life stopped. The stopped dead stay stopped; the gallery names who restarted it. */
export const WORK_REOPENED = 'legacy:work-reopened';

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];
/** The journal's opening note for a later life: counts lives honestly and names who came before, dead or alive. */
export function arrivalEntry(continuity: Continuity, ordinal: number): StoryJournalEntry | null {
  const last = continuity.closedRuns.at(-1);
  if (!last) return null;
  const survivor = [...continuity.closedRuns].reverse().find(run => run.outcome === 'survived' && continuity.persons[run.pcId]?.lifecycle !== 'dead');
  const text = [ORDINALS[ordinal - 1] ? `A ${ORDINALS[ordinal - 1]} life begins at the landing.` : `Life ${ordinal} begins at the landing.`,
    ...(last.outcome === 'died' ? [`${continuity.persons[last.pcId]!.current.name} did not survive the last one. The dead remain dead.`] : []),
    ...(survivor ? [`${continuity.persons[survivor.pcId]!.current.name} is at the Quiet Kilns, in the Dry Cut — possessions and promises still their own.`] : [])].join(' ');
  return { id: `second-arrival:${ordinal}`, kind: 'discovery', text };
}

/** What a living PC has done: their acts, the chapter's `remembered` facts this life holds, and the openings it was dealt. */
export function lifeDeeds(content: StoryContent, state: StorySession, deeds: readonly string[]): string[] {
  const remembered = (content.remembered ?? []).map(memory => memory.fact).filter(fact => state.slice.quest.facts.includes(fact));
  return [...new Set([...deeds, ...state.completed, ...remembered, ...state.slice.quest.facts.filter(fact => fact.startsWith('opening:'))])];
}

/** Every closed life's deeds, the most recent first: what the next life's openings are dealt against. */
export const pastLives = (continuity: Continuity): string[][] =>
  [...continuity.closedRuns].reverse().map(run => continuity.persons[run.pcId]?.current.deeds ?? []);

/**
 * Every successor name, in order: the authored stock, then names built from its parts once the stock runs out, so a
 * chronology never runs out of distinct names. A later name keeps an authored first name and joins authored family
 * names into one ("Arel Belgath", then "Arel Belgathkall"), so it still reads as a first name and a family name.
 */
export function* successorNames(stock: readonly string[]): Generator<string> {
  yield* stock;
  const firsts = [...new Set(stock.map(name => name.split(' ')[0]!))];
  const families = [...new Set(stock.map(name => name.split(' ').at(-1)!))];
  if (!firsts.length) throw new Error('The successor-name recipe produced no names.');
  for (let joined = families; ; ) {
    joined = joined.flatMap(family => families.filter(next => !family.toLowerCase().endsWith(next.toLowerCase())).map(next => family + next.toLowerCase()));
    for (const family of joined) for (const first of firsts) yield `${first} ${family}`;
  }
}

/** The first successor name, in `order` and then past the authored stock, that `free` accepts. */
export function freeSuccessorName(stock: readonly string[], order: readonly string[], free: (name: string) => boolean): string {
  const name = order.find(free);
  if (name) return name;
  for (const candidate of successorNames(stock)) if (free(candidate)) return candidate;
  throw new Error('unreachable: successor names are unbounded');
}

/**
 * A later life's new contract, against the ledger of every life before it. The concession works the station the dealt
 * opening names. A body an earlier life stopped stays stopped: the contract has bought other dead, with names of their
 * own, to work in its place. Every other station works only with dead the ledger still has at work; the rest lie idle.
 * Call on a fresh opening before it joins the ledger.
 */
export function reopenUnderContract(content: StoryContent, story: StorySession, ledger: Continuity, ordinal: number): void {
  const contract = Object.values(content.zoneOpenings ?? {}).flat().find(opening => opening.station && story.slice.quest.facts.includes(`opening:${opening.id}`));
  if (!contract) return;
  let continuity = story.slice.continuity;
  const operation = structuredClone(story.slice.operation);
  const names = new Set([...Object.values(ledger.persons), ...Object.values(continuity.persons)].map(person => person.current.name));
  for (const station of operation.stations) for (const body of station.bodies) {
    const before = ledger.remains[body.remainsId];
    if (station.id !== contract.station) {
      if (!before) continuity = changeRemains(continuity, body.remainsId, 'exposed');
    } else if (before && before.disposition !== 'animated') {
      const personId = `${before.personId.replace(/-life-\d+$/, '')}-life-${ordinal}`;
      // Stride through the stock so the new dead of one contract, and of the next, do not share a first name.
      const stock = content.successorNames;
      const name = freeSuccessorName(stock, stock.map((_, i) => stock[(ordinal * 37 + i * 11) % stock.length]!), candidate => !names.has(candidate));
      names.add(name);
      continuity = killPerson(registerPerson(continuity, makePerson(personId, name, 'Z04')), personId, `event:burial-${personId.slice('person:'.length)}`);
      body.remainsId = `remains:${personId.slice('person:'.length)}`;
      continuity = changeRemains(continuity, body.remainsId, 'animated');
    }
  }
  story.slice = { ...story.slice, continuity, operation };
}

/** A compact prototype closure uses the same irreversible ledger as durable lives.
 * It cannot manufacture production mystery observations or transfer private knowledge. */
export function closeStoryLife(content: StoryContent, input: StorySession): StorySession {
  const state = structuredClone(input);
  const pc = state.slice.continuity.persons[storyPcId(state)]!;
  if (state.slice.continuity.closedRuns.some(run => run.pcId === pc.id)) throw new Error('This life was already closed.');
  if (pc.lifecycle !== 'dead' && (state.beatId !== 'z04-dry-cut' || !runEnded(state.slice.quest.facts))) throw new Error('See the aftermath and reach the still place before closing this life.');
  const ordinal = state.slice.continuity.closedRuns.length + 1;
  pc.current.personalOutcome = state.slice.report?.personalGoal.summary ?? content.deathFinding;
  pc.current.deeds = lifeDeeds(content, state, pc.current.deeds);
  let continuity = closeRun(state.slice.continuity, {
    id: `run:${ordinal}`, chronologyId: state.slice.continuity.chronologyId, ordinal,
    namespace: state.slice.continuity.namespace, rulesetId: 'd00-contract-bench',
    pcId: pc.id, outcome: pc.lifecycle === 'dead' ? 'died' : 'survived', actions: [],
  }, []);
  continuity = validateLedger(continuity);
  state.slice = { ...state.slice, continuity, quest: applyEffects(state.slice.quest, { add: ['story:life-closed'], remove: [] }) };
  state.dialogue = null;
  return state;
}

/** Continue only an actual closed story. The old ledger is retained in full.
 * Named corpses and dead companions never re-enter an opening as living people. */
export function nextStorySession(content: StoryContent, previous: StorySession, options: Parameters<typeof createStorySession>[1] = {}): StorySession {
  if (previous.slice.continuity.persons[storyPcId(previous)]?.lifecycle === 'dead' && !previous.slice.quest.facts.includes('story:life-closed')) previous = closeStoryLife(content, previous);
  const frozen = validateLedger(previous.slice.continuity);
  if (!previous.slice.quest.facts.includes('story:life-closed') || frozen.closedRuns.at(-1)?.pcId !== storyPcId(previous)) throw new Error('Close the previous life before starting another.');
  if (frozen.namespace !== 'prototype' || frozen.settlement !== 'folded') throw new Error('Compact stories continue only an unsettled prototype chronology.');
  const ordinal = frozen.closedRuns.length + 1;
  const fresh = createStorySession(content, { ...options, past: pastLives(frozen) });
  const bindings: Record<string, string> = { 'person:pc': `person:pc-life-${ordinal}` };
  const castNames: Record<string, string> = {};
  const usedNames = new Set(Object.values(frozen.persons).map(person => person.current.name));
  const successor = (person: Person): void => {
    bindings[person.id] = `${person.id}-life-${ordinal}`;
    const name = freeSuccessorName(content.successorNames, content.successorNames, name => !usedNames.has(name));
    usedNames.add(name); castNames[person.current.name] = name;
  };
  for (const person of Object.values(fresh.slice.continuity.persons)) {
    if (person.id === 'person:pc') continue;
    const existing = frozen.persons[storyIdentity(previous, person.id)];
    // The new concession has new victims. Archived remains keep their actual disposition.
    if (person.lifecycle === 'dead' || existing?.lifecycle === 'dead' || existing?.original) successor(person);
    else if (existing) bindings[person.id] = existing.id;
    if (person.lifecycle === 'dead') bindings[`remains:${person.id.slice(7)}`] = `remains:${bindings[person.id]!.slice(7)}`;
  }
  for (const [short, full] of Object.entries(content.shortNames)) {
    const name = castNames[full];
    if (name) castNames[short] = short === full.split(' ').at(-1) ? name.split(' ').at(-1)! : name.split(' ')[0]!;
  }
  // A killed guard cannot return just because their fight has not started yet.
  bindings['person:kiln-office-guard'] = `person:kiln-office-guard-life-${ordinal}`;
  const remap = (value: unknown): unknown => {
    if (typeof value === 'string') return bindings[value] ?? castNames[value] ?? value;
    if (Array.isArray(value)) return value.map(remap);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, child]) => [bindings[key] ?? key, remap(child)]));
    return value;
  };
  const state = remap(fresh) as StorySession;
  state.bindings = bindings; state.castNames = castNames;
  let continuity = structuredClone(frozen);
  for (const person of Object.values(state.slice.continuity.persons)) {
    const existing = continuity.persons[person.id];
    if (!existing) {
      person.lineageKey = person.id; person.presentationKey = `presentation:${person.id}`;
      if (person.deathEventId) person.deathEventId = `event:burial-${person.id}`;
      person.current.originalRunId = `run:${ordinal}`; person.current.latestRunId = `run:${ordinal}`;
      continuity.persons[person.id] = person;
    } else if (!existing.original) existing.location = person.location;
  }
  Object.assign(continuity.remains, state.slice.continuity.remains);
  for (const id of [...continuity.legacyIds, ...continuity.supportingIds]) if (continuity.persons[id]!.lifecycle !== 'dead') continuity = demanifest(continuity, id);
  const survivor = [...continuity.closedRuns].reverse().find(run => run.outcome === 'survived' && continuity.persons[run.pcId]?.lifecycle !== 'dead');
  if (survivor) {
    continuity = bindLiving(continuity, survivor.pcId, 'legacy', 'Z04');
    state.legacyId = survivor.pcId;
  }
  const entry = arrivalEntry(continuity, ordinal);
  if (entry) { state.journal.push(entry); state.events.push({ type: 'journal', sequence: state.events.length, entry }); }
  continuity.date = { ...OPENING_DATE };
  assertContinuityTransition(frozen, continuity);
  state.slice = { ...state.slice, continuity: validateLedger(continuity) };
  for (const candidate of state.candidates) candidate.person = structuredClone(continuity.persons[candidate.person.id]!);
  // Every opening here is a new concession with new victims, so stations an earlier life stopped have been reopened.
  if (previous.slice.operation.stations.some(station => station.integrity !== 'intact')) state.slice = { ...state.slice, quest: applyEffects(state.slice.quest, { add: [WORK_REOPENED], remove: [] }) };
  return withContinuityLeads(content, state);
}
